import { useEffect, useId, useRef, useState } from 'react'
import { nextTab } from '../tabs'
import type { Cluster } from '../types'
import { imageUrl } from '../api'
import { isInProgress } from '../activity'
import { useFinishTick } from '../useFinishTick'
import { relTime } from '../relTime'
import { dayGroups } from '../eventDays'

// The image is attached after its event is stored, so it 404s until that lands —
// over WireGuard for an ONVIF site, which is well past the first retry. Backing
// off matters because `failed` only clears when the key changes, and a settled
// cluster's key never does: giving up early blanks the thumbnail until reload.
const RETRY_DELAYS_MS = [1500, 3000, 6000, 12000]

function ClusterThumb({ cluster }: { cluster: Cluster }) {
  // Keyed by the snapshot identity so a new one starts fresh by derivation,
  // rather than being reset from an effect.
  const key = `${cluster.id}:${cluster.end_time}`
  const [state, setState] = useState({ key, retry: 0, failed: false })
  const { retry, failed } = state.key === key ? state : { retry: 0, failed: false }
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const handleError = () => {
    const delay = RETRY_DELAYS_MS[retry]
    if (delay === undefined) {
      setState({ key, retry, failed: true })
      return
    }
    timer.current = setTimeout(() => setState({ key, retry: retry + 1, failed: false }), delay)
  }

  return (
    <div className="relative shrink-0 w-24 h-[54px] rounded-md overflow-hidden bg-zinc-950">
      {failed ? (
        <div className="w-full h-full flex items-center justify-center text-[11px] text-zinc-400">No picture</div>
      ) : (
        <img
          key={retry}
          src={`${imageUrl(cluster.primary_event_id ?? cluster.last_event_id, cluster.end_time)}${retry ? `&r=${retry}` : ''}`}
          alt=""
          width={96}
          height={54}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={handleError}
        />
      )}
    </div>
  )
}

function AlertCard({ cluster: c, selected, isNew, onClick }: { cluster: Cluster; selected: boolean; isNew: boolean; onClick: () => void }) {
  const tamper = c.label === 'tamper'
  const inProgress = isInProgress(c)
  return (
    <li
      aria-current={selected ? 'true' : undefined}
      className={`relative rounded-lg border overflow-hidden transition-colors ${
        selected
          ? 'border-orange-400 bg-orange-500/10'
          : tamper && !c.acknowledged
            ? 'border-attention/55 bg-attention/[0.06]'
            : 'border-zinc-800 bg-zinc-900'
      }`}
    >
      {isNew && <div className="flash-new absolute inset-0 pointer-events-none rounded-lg" />}
      <button onClick={onClick} className="w-full flex gap-3 p-2.5 text-left hover:bg-zinc-800/50 transition-colors">
        <ClusterThumb cluster={c} />
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="text-sm font-semibold flex items-center gap-1.5 min-w-0">
            {/* Amber and named, not just first in the list: a card that is
                merely at the top reads as the most recent one. */}
            {tamper && (
              <svg data-testid="tamper-marker" aria-hidden="true" className="w-3.5 h-3.5 shrink-0 text-attention" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            )}
            <span className={`truncate ${tamper ? 'text-attention' : 'text-zinc-100'}`}>{cardLabel(c)}</span>
            {c.score > 0 && <span className="shrink-0 text-xs font-mono font-medium text-zinc-400">{Math.round(c.score * 100)}%</span>}
          </div>
          <div className="text-[13px] text-zinc-400 truncate">
            {c.site_name || c.site_id} · {c.camera}
          </div>
          <div className="flex items-center gap-2.5 text-xs font-mono text-zinc-400 whitespace-nowrap">
            {inProgress && (
              <span className="flex items-center gap-1 text-live">
                <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-current" />
                <span>In progress</span>
              </span>
            )}
            <span>{relTime(c.end_time)}</span>
            {c.event_count > 1 && <span>{c.event_count} detections</span>}
            {c.reported_late && <span className="text-zinc-300">Reported late</span>}
          </div>
        </div>
      </button>
    </li>
  )
}

// cardLabel is an incident's label as the rail shows it, tampering as what the
// dispatcher has lost, as mobile's Events list says it.
function cardLabel(c: Cluster): string {
  if (c.label === 'tamper') return 'View lost'
  const words = (c.label || 'detection').replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

interface Props {
  clusters: Cluster[]
  newIds: Set<number>
  selectedId: number | null
  status: 'loading' | 'ok' | 'error' | 'live'
  error: string
  onClusterClick: (cluster: Cluster) => void
  // Whether a site/camera filter is narrowing this list. Shown here because a
  // short list is otherwise indistinguishable from a quiet one.
  narrowed: boolean
  open: boolean
  onToggle: () => void
  /**
   * The rail beside the wall on a desktop; a drawer over it on a tablet, whose
   * toggle closes it; or a phone's Alerts tab, with nothing to collapse (#284).
   */
  variant?: 'rail' | 'drawer' | 'page'

  // disarmedSites are named at the top of the Pending tab whatever it holds,
  // as mobile does above Events (#236, #248), with when each next arms.
  disarmedSites?: { id: string; name: string; arms?: string }[]
}

const connection = {
  live: { words: 'Live', text: 'text-live' },
  ok: { words: 'Connected', text: 'text-live' },
  loading: { words: 'Connecting…', text: 'text-zinc-400' },
  error: { words: 'Disconnected', text: 'text-fault' },
} as const

// Bell, matching the outline icon set the rest of the chrome uses.
const ALERT_ICON_PATH =
  'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0'

// Capped so a detection burst can't render hundreds of <img>s.
const MAX_VISIBLE = 40

// pinTampered lifts tampering above the time-ordered list, newest first within
// the pin. A blinded camera at a quiet site would otherwise sink under an hour
// of cars at a busy one, which is backwards — and this is safe only because
// tampering is rare. Pinning a common category would wreck "what just
// happened", so nothing else should be pinned casually.
//
// Only while pending: acknowledged means someone has seen it, and a resolved
// incident held at the top forever is its own kind of noise.
function pinTampered(clusters: Cluster[]): Cluster[] {
  const tampered = clusters.filter((c) => c.label === 'tamper')
  if (tampered.length === 0) return clusters
  return [...tampered, ...clusters.filter((c) => c.label !== 'tamper')]
}

type Tab = 'pending' | 'acknowledged'
const tabs: readonly Tab[] = ['pending', 'acknowledged']

export function ClustersSidebar({ clusters, newIds, selectedId, status, error, onClusterClick, narrowed, open: railOpen, onToggle, disarmedSites = [], variant = 'rail' }: Props) {
  const open = railOpen || variant !== 'rail'
  const tabIds = useId()
  // Collapsing swaps the button pressed for its opposite; focus follows rather
  // than falling to the page (#287). Only when focus was lost with the button.
  const toggle = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(open)
  useEffect(() => {
    if (wasOpen.current !== open && document.activeElement === document.body) toggle.current?.focus()
    wasOpen.current = open
  }, [open])
  useFinishTick(clusters)
  // A tab click applies until the selection changes; selecting a cluster then
  // moves the tab to whichever list holds it. Derived rather than synced from an
  // effect, which caused a cascading render.
  const [picked, setPicked] = useState<{ key: string; tab: Tab } | null>(null)

  const unacknowledged = clusters.filter((c) => !c.acknowledged)
  const acknowledged = clusters.filter((c) => c.acknowledged)
  const selectedAck = clusters.find((c) => c.id === selectedId)?.acknowledged
  const selectionKey = `${selectedId}:${selectedAck}`
  const followSelection: Tab | null =
    selectedId != null && selectedAck !== undefined ? (selectedAck ? 'acknowledged' : 'pending') : null
  const tab: Tab = picked?.key === selectionKey ? picked.tab : (followSelection ?? 'pending')
  const setTab = (t: Tab) => setPicked({ key: selectionKey, tab: t })

  const filtered = tab === 'pending' ? pinTampered(unacknowledged) : acknowledged
  const visible = filtered.slice(0, MAX_VISIBLE)
  // Pending tampering keeps its pin under a heading of its own, so its place
  // at the top reads as why it is there rather than as the newest.
  const pinned = tab === 'pending' ? visible.filter((c) => c.label === 'tamper') : []
  const groups = [
    ...(pinned.length ? [{ day: 'Needs attention', clusters: pinned, attention: true }] : []),
    ...dayGroups(visible.filter((c) => !pinned.includes(c))).map((g) => ({ ...g, attention: false })),
  ]

  // Collapsed, an icon says what the rail is, the whole button expands it, and
  // the pending count survives — it is the one number worth glancing at, and
  // hiding it made nothing pending and a backlog look identical.
  // The button is pinned to the rail's final width rather than filling it: the
  // panel animates from 352px down to 32px, and a w-full button rides that the
  // whole way, so collapsing flashed a 265px orange block instead of reading as
  // a panel closing (measured mid-animation).
  //
  // One <aside> whose width class changes, never two that swap: React
  // unmounting one element and mounting another leaves nothing for
  // transition-[width] to animate, and the rail snapped open instead of
  // sliding.
  return (
    <aside
      className={`shrink-0 flex flex-col bg-zinc-900 overflow-hidden ${
        variant === 'rail' ? `border-r border-zinc-800 transition-[width] duration-200 motion-reduce:transition-none ${open ? 'w-88' : 'w-8'}` : 'w-full h-full'
      }`}
    >
      {!open ? (
          <button
            ref={toggle}
            onClick={onToggle}
            title="Show alerts"
            aria-label={['Show alerts', unacknowledged.length > 0 && `${unacknowledged.length} pending`, disarmedSites.length > 0 && `${disarmedSites.length} ${disarmedSites.length === 1 ? 'site' : 'sites'} disarmed`].filter(Boolean).join(', ')}
            className="w-8 shrink-0 flex flex-col items-center gap-1.5 py-3 bg-orange-400 hover:bg-orange-300 text-zinc-950 shadow-sm transition-colors"
          >
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={ALERT_ICON_PATH} />
            </svg>
            {unacknowledged.length > 0 && (
              <span
                data-testid="collapsed-pending-count"
                className="text-[10px] font-bold leading-none tabular-nums"
              >
                {unacknowledged.length}
              </span>
            )}
            {/* The collapsed Sites sidebar counted these (#236); a collapsed
                rail otherwise left nothing to say a site raises no alerts. */}
            {disarmedSites.length > 0 && (
              <span data-testid="collapsed-disarmed-count" className="mt-1 px-1 rounded bg-zinc-950 text-attention text-[10px] font-bold leading-4 tabular-nums">
                {disarmedSites.length}
              </span>
            )}
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>


      ) : (
        <>
        <div className="shrink-0 flex items-center justify-between gap-2 px-4 pt-4 pb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <h2 className="text-base font-semibold text-zinc-100">Alerts</h2>
            {narrowed && (
              <span
                title="A site or camera filter is narrowing this list"
                className="shrink-0 h-5 px-1.5 inline-flex items-center rounded text-[11px] font-semibold text-orange-300 bg-orange-500/15 border border-orange-500/30"
              >
                Filtered
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {/* Said, not only coloured: a dot alone was the rail's only sign it
                had stopped receiving. */}
            <span
              className={`flex items-center gap-1.5 text-xs font-mono ${connection[status].text}`}
              title={status === 'error' ? `Disconnected: ${error || 'no connection'}` : undefined}
            >
              <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full bg-current ${status === 'loading' ? 'animate-pulse motion-reduce:animate-none' : ''}`} />
              {connection[status].words}
            </span>
            {variant !== 'page' && (
              <button
                ref={toggle}
                onClick={onToggle}
                className="w-8 h-8 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
                title={variant === 'drawer' ? 'Close alerts' : 'Collapse alerts'}
                aria-label={variant === 'drawer' ? 'Close alerts' : 'Collapse alerts'}
              >
                <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={variant === 'drawer' ? 'M18 6 6 18M6 6l12 12' : 'M15 19l-7-7 7-7'} />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div role="tablist" aria-label="Alert queue" className="shrink-0 mx-3 mb-3 grid grid-cols-2 gap-1 p-1 rounded-lg border border-zinc-800 bg-zinc-950">
          {tabs.map((t) => (
            <button
              key={t}
              id={`${tabIds}-${t}`}
              role="tab"
              aria-selected={tab === t}
              aria-controls={`${tabIds}-panel`}
              tabIndex={tab === t ? 0 : -1}
              onKeyDown={(e) => {
                const next = nextTab(e.key, tabs, tab)
                if (!next) return
                e.preventDefault()
                setTab(next)
                document.getElementById(`${tabIds}-${next}`)?.focus()
              }}
              // Spelt out: "Pending" and its badge otherwise read as "Pending2".
              aria-label={t === 'pending' && unacknowledged.length > 0 ? `Pending ${unacknowledged.length}` : undefined}
              onClick={() => setTab(t)}
              className={`h-8 max-sm:h-11 flex items-center justify-center gap-2 rounded-md text-sm transition-colors ${
                tab === t ? 'bg-zinc-800 text-zinc-100 font-semibold' : 'text-zinc-400 hover:text-zinc-100'
              }`}
            >
              {t === 'pending' ? 'Pending' : 'Acknowledged'}
              {t === 'pending' && unacknowledged.length > 0 && (
                <span className="min-w-5 h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-orange-400 text-zinc-950 text-xs font-bold font-mono">
                  {unacknowledged.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div id={`${tabIds}-panel`} role="tabpanel" aria-labelledby={`${tabIds}-${tab}`} className="flex-1 relative overflow-y-auto scrollbar-thin px-3 pb-3 flex flex-col gap-3">
          {status === 'error' && (
            <p role="alert" className="px-1 text-sm text-fault">Disconnected: {error || 'no connection to server'}. New alerts will not appear until it reconnects.</p>
          )}
          {status === 'loading' && <p className="px-1 text-sm text-zinc-400">Loading alerts…</p>}
          {tab === 'pending' && disarmedSites.map((site) => (
            <div key={site.id} className="flex gap-2.5 px-3 py-2.5 rounded-lg border border-attention/35 bg-attention/10">
              <svg aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0 text-attention" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path strokeLinecap="round" d="M8 11V7a4 4 0 0 1 7.5-2" />
              </svg>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-zinc-100">{site.name} is disarmed</p>
                <p className="text-[13px] text-zinc-400">
                  {site.arms ? `Alerts paused until ${site.arms}. Tampering is still reported.` : 'Alerts paused. Tampering is still reported.'}
                </p>
              </div>
            </div>
          ))}

          {status !== 'loading' && visible.length === 0 ? (
            <p className="py-6 text-sm text-zinc-400 text-center">
              {tab === 'pending' ? 'No pending alerts' : 'No acknowledged alerts'}
            </p>
          ) : (
            groups.map((g) => (
              <section key={g.day} className="flex flex-col gap-1.5">
                <h3 className={`px-1 text-xs font-mono uppercase tracking-wider ${g.attention ? 'text-attention' : 'text-zinc-400'}`}>{g.day}</h3>
                <ul className="flex flex-col gap-1.5">
                  {g.clusters.map((c) => (
                    <AlertCard key={c.id} cluster={c} selected={c.id === selectedId} isNew={newIds.has(c.id)} onClick={() => onClusterClick(c)} />
                  ))}
                </ul>
              </section>
            ))
          )}

          {filtered.length > MAX_VISIBLE && (
            <p className="py-2 text-xs text-zinc-400 text-center">
              +{filtered.length - MAX_VISIBLE} older alerts not shown
            </p>
          )}
        </div>
        </>
      )}
    </aside>
  )
}
